import { useActionFailedAlert } from 'shared/problems/composables/actionFailedAlert';
import type { ProblemDetails } from 'shared/problems/domain/problemDetails';
import { Assert } from 'shared/assert';
import {
  useInventoryStorageArea,
  type InventoryStorageArea,
} from '@/storageAreas/api/inventoryStorageArea';
import { StorageAreaType } from 'shared/storageAreas/domain/storageAreaType';
import { defineStore, storeToRefs } from 'pinia';
import { ref, shallowRef, watch } from 'vue';
import { useUndoStore } from 'shared/undo/stores/undoStore';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { useUndoInventoryApi } from '@/storageAreas/api/undoInventoryApi';
import { ProductType } from 'shared/products/domain/productType';
import { useProductStore } from 'shared/products/stores/productStore';
import { useRefereshProductStore } from '@/products/commands/refreshProductStore';
import { useStorageAreaStore } from 'shared/storageAreas/stores/storageAreaStore';
import type { LogYardData } from 'shared/storageAreas/domain/storageArea';

export interface ShowDialog {
  storageAreaId: string;
  productId?: string;
}

export interface ProductContent {
  product: Product;
  numberOfLogs: number;
  volume: number;
  shouldSave: boolean;
}

export interface Product {
  id: string;
  name: string;
}

export const useEditInventoryStore = defineStore('editInventory', () => {
  const storageAreaStore = useStorageAreaStore();
  const productStore = useProductStore();
  const inventoryStorageArea = useInventoryStorageArea();
  const undoStore = useUndoStore();
  const languageStore = useLanguageStore();
  const { translations } = storeToRefs(languageStore);
  const undoInventoryApi = useUndoInventoryApi();
  const refreshProductStore = useRefereshProductStore();

  const visible = ref<boolean>(false);
  const storageAreaId = ref<string>();
  const storageAreaType = ref<StorageAreaType>(StorageAreaType.Undefined);
  const storageAreaName = ref<string>();
  const logYard = ref<LogYardData | null>(null);
  const selectedProductId = ref<string | null>(null);
  const selectedProductName = ref<string | null>(null);
  const currentCount = ref<number>(0);
  const currentVolume = ref<number>(0);
  const newCount = ref<number>(0);
  const productOptions = shallowRef<Product[]>([]);
  const contentByProduct = new Map<string, ProductContent>();
  const problems = ref<ProblemDetails>();
  const actionFailedAlert = useActionFailedAlert();

  watch(selectedProductId, (productId) => {
    productSelected(productId);
  });

  const saving = ref(false);

  async function show(options: ShowDialog) {
    selectedProductId.value = null;
    selectedProductName.value = null;
    currentCount.value = 0;
    currentVolume.value = 0;
    newCount.value = 0;
    problems.value = undefined;
    productOptions.value = [];
    contentByProduct.clear();

    const storageArea = storageAreaStore.getById(options.storageAreaId);

    Assert.definied(storageArea);

    const logYardData = storageArea.logYardData;
    Assert.truthy(
      logYardData,
      "Can't edit log yard inventory on a storage area that is not a log yard!",
    );

    logYard.value = logYardData;
    storageAreaId.value = storageArea.id;
    storageAreaType.value = storageArea.type;
    storageAreaName.value = storageArea.name;

    await refreshProductStore.initialize();
    for (const productContent of logYardData.content) {
      const product = productStore.getById(productContent.productId);
      if (!product) continue;
      contentByProduct.set(product.id, {
        product: product,
        numberOfLogs: productContent.numberOfLogs,
        volume: productContent.volume,
        shouldSave: true,
      });
    }

    if (logYardData.allowedProducts.length === 0) {
      for (const product of productStore.products) {
        if (product.type !== ProductType.Log) continue;
        if (contentByProduct.has(product.id)) continue;
        contentByProduct.set(product.id, {
          product,
          numberOfLogs: 0,
          volume: 0,
          shouldSave: false,
        });
      }
    } else {
      for (const productId of logYardData.allowedProducts) {
        const product = productStore.getById(productId);
        if (!product) continue;
        if (contentByProduct.has(product.id)) continue;
        contentByProduct.set(product.id, {
          product,
          numberOfLogs: 0,
          volume: 0,
          shouldSave: false,
        });
      }
    }

    const products = Array.from(contentByProduct.values()).map((c) => c.product);
    products.sort((a, b) =>
      a.name.localeCompare(b.name, undefined, {
        numeric: true,
      }),
    );

    productOptions.value = products;

    const productId = options.productId;

    if (productId !== undefined) {
      selectProduct(productId);
    } else if (products.length === 1) {
      const productId = products[0]!.id;
      selectProduct(productId);
    } else if (contentByProduct.size > 0) {
      const content = Array.from(contentByProduct.values()).sort(
        (a, b) => b.numberOfLogs - a.numberOfLogs,
      )[0];
      if (content && content.numberOfLogs > 0) {
        selectProduct(content.product.id);
      }
    }

    visible.value = true;
  }

  function selectProduct(productId: string) {
    selectedProductId.value = productId;
    productSelected(productId);
  }

  function productSelected(productId: string | null) {
    if (!productId) {
      currentCount.value = 0;
      currentVolume.value = 0;
      newCount.value = 0;
      selectedProductName.value = null;
      return;
    }

    const product = productStore.getById(productId);
    Assert.definied(product, 'Product not found, this should not be possible');
    selectedProductName.value = product.name;

    const content = contentByProduct.get(productId);
    Assert.truthy(content, 'Product content not found, this should not be possible!');

    currentCount.value = content.numberOfLogs;
    newCount.value = content.numberOfLogs;
    currentVolume.value = content.volume;
  }

  async function save() {
    const productId = selectedProductId.value;

    Assert.truthy(productId, 'Product missing!');
    Assert.truthy(storageAreaId.value, 'Storage area missing!');

    try {
      saving.value = true;

      const product = productStore.getById(productId);
      const averagePhysicalVolume = product?.logData?.averagePhysicalVolume ?? 0;

      const request: InventoryStorageArea = {
        storageAreaId: storageAreaId.value,
        storageAreaType: storageAreaType.value,
        logYardData: [
          {
            numberOfLogs: newCount.value,
            productId: productId,
            volume: averagePhysicalVolume * newCount.value,
          },
        ],
      };

      Assert.truthy(request.logYardData, 'Log Yard data is missing');
      for (const content of contentByProduct.values()) {
        if (content.product.id === productId || !content.shouldSave) continue;
        request.logYardData.push({
          numberOfLogs: content.numberOfLogs,
          productId: content.product.id,
          volume: content.volume,
        });
      }

      const totalNumberOfLogs = request.logYardData
        .map((c) => c.numberOfLogs)
        .reduce((a, b) => a + b);

      const response = await inventoryStorageArea.execute(request);

      if (!response.ok) {
        problems.value = response.details;
        return;
      }

      if (totalNumberOfLogs === 0) {
        undoStore.add({
          title: translations.value.undo.inventoryTitle,
          description: translations.value.undo.getUndoInventoryDescription(
            product?.name ?? 'Unknown',
            currentCount.value,
            storageAreaName.value ?? 'Unknown',
          ),
          function: async () => {
            const response = await undoInventoryApi.execute();

            if (!response.ok) {
              actionFailedAlert.show(response.details);
              return;
            }

            storageAreaStore.add(response.storageArea);
          },
        });
      } else {
        const undoRequest: InventoryStorageArea = {
          storageAreaId: storageAreaId.value,
          storageAreaType: storageAreaType.value,
          logYardData: [
            {
              numberOfLogs: currentCount.value,
              productId: productId,
              volume: currentVolume.value,
            },
          ],
          undo: true,
        };

        for (const content of contentByProduct.values()) {
          if (content.product.id === productId) continue;
          request.logYardData.push({
            numberOfLogs: content.numberOfLogs,
            productId: content.product.id,
            volume: content.volume,
          });
        }

        undoStore.add({
          title: translations.value.undo.inventoryTitle,
          description: translations.value.undo.getUndoInventoryDescription(
            product?.name ?? 'Unknown',
            currentCount.value,
            storageAreaName.value ?? 'Unknown',
          ),
          function: async () => {
            const response = await inventoryStorageArea.execute(undoRequest);

            if (!response.ok) {
              actionFailedAlert.show(response.details);
              return;
            }

            storageAreaStore.add(response.storageArea);
          },
        });
      }

      storageAreaStore.add(response.storageArea);
      storageAreaId.value = undefined;
      storageAreaType.value = StorageAreaType.Undefined;
      storageAreaName.value = undefined;
      visible.value = false;
    } finally {
      saving.value = false;
    }
  }

  return {
    show,
    save,
    visible,
    problems,
    selectedProductId,
    currentCount,
    newCount,
    productOptions,
    saving,
    logYard,
    storageAreaName,
    selectedProductName,
  };
});
